import { useEffect, useRef } from 'react';
import { useTimerStore } from '../store';
import { onSettings, saveSettings } from '../firebase';

/**
 * Settings live in users/{uid}/settings/main and are shared by the PWA, the extension popup and
 * the focus window: a change on one device shows up on the others at once (before, the extension
 * kept its own copy and each device computed breaks with different durations).
 */
export function useSettingsSync(uid?: string) {
  const settings = useTimerStore((s) => s.settings);
  const updateSettings = useTimerStore((s) => s.updateSettings);
  const loaded = useRef(false);
  const lastCloud = useRef('');

  useEffect(() => {
    loaded.current = false;
    lastCloud.current = '';
    if (!uid) return;
    return onSettings(uid, (cloud) => {
      const local = useTimerStore.getState().settings;
      if (!cloud) {
        // No settings in the cloud yet: this device's become the shared ones.
        loaded.current = true;
        void saveSettings(uid, local).catch(() => {});
        lastCloud.current = JSON.stringify(local);
        return;
      }
      const merged = { ...local, ...cloud };
      lastCloud.current = JSON.stringify(merged);
      if (JSON.stringify(local) !== lastCloud.current) updateSettings(merged);
      loaded.current = true;
    }, () => { loaded.current = true; });   // offline at start: keep local ones and save later
  }, [uid, updateSettings]);

  useEffect(() => {
    if (!uid || !loaded.current) return;
    const json = JSON.stringify(settings);
    if (json === lastCloud.current) return;
    const id = setTimeout(() => {
      lastCloud.current = json;
      void saveSettings(uid, settings).catch(() => { lastCloud.current = ''; });
    }, 700);
    return () => clearTimeout(id);
  }, [settings, uid]);
}
